import { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/react';
import { mockPatients, mockExamsByPatient } from '@/data/mock';
import { DetailHeader } from '@/components/detail/DetailHeader';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import {
  OverviewSection,
  NotesSection,
  NotesSplitPanel,
  ExamsSection,
  ImagingsSectionPlaceholder,
  VitalsSection,
  CommunicationSection,
  OrdersSection,
  DocumentsSection,
  INITIAL_NOTES,
  type Note,
} from '@/components/detail/EhrSections';

const TAB_LABELS = [
  'Resumen',
  'Notas',
  'Laboratorios',
  'Imágenes',
  'Signos vitales',
  'Órdenes',
  'Documentos',
  'Comunicación',
];

const tabClass = ({ selected }: { selected: boolean }) =>
  [
    'font-inherit text-[12.5px] px-3.5 py-2 cursor-pointer outline-none transition-colors rounded-md whitespace-nowrap',
    selected
      ? 'bg-primary text-surface font-semibold'
      : 'bg-transparent text-tertiary font-medium hover:text-primary hover:bg-soft',
  ].join(' ');

export function PatientDetail() {
  const { id } = useParams<{ id: string }>();
  const patient = mockPatients.find((p) => p.id === id);
  const [tabIndex, setTabIndex] = useState(0);
  const [notes, setNotes] = useState<Note[]>(INITIAL_NOTES);
  const [splitNotes, setSplitNotes] = useState(false);

  if (!patient) {
    return <Navigate to="/patients" replace />;
  }

  const exams = mockExamsByPatient[patient.id] ?? [];
  const reportUrl = `/patients/${patient.id}/report`;

  const handleAddNote = (n: Note) => setNotes((prev) => [n, ...prev]);
  const toggleSplit = () => setSplitNotes((v) => !v);

  return (
    <main>
      <div
        className={`pane-content appear transition-all ${
          splitNotes ? 'max-[760px]:pb-[60vh] min-[760px]:pr-[420px] min-[1024px]:pr-[420px]' : ''
        }`}
      >
        <Breadcrumb
          className="mb-3.5"
          items={[
            { label: 'Pacientes', href: '/patients' },
            { label: patient.name, href: `/patients/${patient.id}` },
            { label: TAB_LABELS[tabIndex] },
          ]}
        />

        <DetailHeader
          patient={patient}
          reportUrl={reportUrl}
          extraAction={
            <button
              type="button"
              onClick={() => setSplitNotes((v) => !v)}
              title={splitNotes ? 'Cerrar panel de notas' : 'Abrir notas en panel split'}
              className={`inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-2 rounded-md cursor-pointer transition-colors ${
                splitNotes
                  ? 'bg-primary text-surface border border-primary'
                  : 'bg-transparent border border-line-strong text-secondary hover:bg-soft hover:border-line-default hover:text-primary'
              }`}
            >
              <SplitButtonIcon />
              {splitNotes ? 'Cerrar notas' : 'Notas en split'}
            </button>
          }
        />

        <TabGroup selectedIndex={tabIndex} onChange={setTabIndex}>
          <TabList className="glass-panel flex gap-1 p-1.5 mb-4 self-start max-[760px]:overflow-x-auto">
            <Tab className={tabClass}>Resumen</Tab>
            <Tab className={tabClass}>Notas</Tab>
            <Tab className={tabClass}>Laboratorios</Tab>
            <Tab className={tabClass}>Imágenes</Tab>
            <Tab className={tabClass}>Signos vitales</Tab>
            <Tab className={tabClass}>Órdenes</Tab>
            <Tab className={tabClass}>Documentos</Tab>
            <Tab className={tabClass}>Comunicación</Tab>
          </TabList>

          <TabPanels>
            <TabPanel className="outline-none appear">
              <OverviewSection patient={patient} exams={exams} reportUrl={reportUrl} />
            </TabPanel>
            <TabPanel className="outline-none appear">
              <NotesSection
                notes={notes}
                onAddNote={handleAddNote}
                splitMode={splitNotes}
                onToggleSplit={toggleSplit}
              />
            </TabPanel>
            <TabPanel className="outline-none appear">
              <ExamsSection patient={patient} exams={exams} reportUrl={reportUrl} />
            </TabPanel>
            <TabPanel className="outline-none appear">
              <ImagingsSectionPlaceholder />
            </TabPanel>
            <TabPanel className="outline-none appear">
              <VitalsSection />
            </TabPanel>
            <TabPanel className="outline-none appear">
              <OrdersSection />
            </TabPanel>
            <TabPanel className="outline-none appear">
              <DocumentsSection />
            </TabPanel>
            <TabPanel className="outline-none appear">
              <CommunicationSection />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>

      {splitNotes && (
        <NotesSplitPanel
          notes={notes}
          onAddNote={handleAddNote}
          onClose={() => setSplitNotes(false)}
          patientName={patient.name}
        />
      )}
    </main>
  );
}

function SplitButtonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M14 4v16" />
    </svg>
  );
}
